import environment from '../../.configs/app/environment'

const normalizePagePath = (pathname) =>
    pathname === '/' ? 'Home' : pathname.slice(1).replaceAll('/', '_')

const isGtagAvailable = () =>
    typeof window !== 'undefined' && typeof window.gtag === 'function'

const ADWORD_ACCOUNT_PATTERN = /^AW-\d+$/

/**
 * Upper bound on waiting for gtag's `event_callback` before running a
 * caller's `onComplete` anyway. The callback can arrive late or never (ad
 * blocker, gtag library not loaded, dead network), and `onComplete` gates
 * user-facing navigation — so it must always run. 500ms comfortably covers
 * the conversion ping round-trip without a perceptible stall.
 */
const ADS_PING_COMPLETE_TIMEOUT_MS = 500

/**
 * Labels that match the shape but mean "nobody filled this in".
 * 'undefined' is what `environment.adwordConversionCodes` concatenates when
 * an env has no Adwords config, or has the account set but not the label.
 */
const PLACEHOLDER_LABELS = new Set([
    'ADD_CODE_HERE',
    'undefined',
])

/** A usable send_to is `AW-<digits>/<label>`. */
const isValidSendTo = (sendTo) => {
    if (typeof sendTo !== 'string') return false

    const [
        account,
        label,
    ] = sendTo.split('/')

    return ADWORD_ACCOUNT_PATTERN.test(account)
        && Boolean(label)
        && !PLACEHOLDER_LABELS.has(label)
}

/**
 * Push a Google Consent Mode v2 update for the four storage types GA4, Google
 * Ads and any GTM-managed tag read.
 *
 * The *defaults* are declared inline in `index.html`, above the GTM snippet —
 * they have to be queued before gtm.js loads, which is earlier than this bundle
 * executes. This function only handles the later `update`, driven by the cookie
 * banner in `consent-context.jsx`.
 *
 * Safe to call before gtag.js has loaded: the inline stub in `index.html`
 * defines `window.gtag`, so the command queues in `dataLayer` and is applied
 * in order once the library arrives.
 *
 * @param {boolean} granted - true on accept, false on decline/revoke.
 */
export const updateGtagConsent = (granted) => {
    if (!isGtagAvailable()) return;

    const value = granted ? 'granted' : 'denied';

    window.gtag('consent', 'update', {
        ad_personalization: value,
        ad_storage: value,
        ad_user_data: value,
        analytics_storage: value,
    });
};

export const initGtag = (ga4MeasurementId) => {
    if (typeof window === 'undefined') return;

    // Guard: Prevent duplicate initialization
    if (window.gtag && document.querySelector(`script[src*="gtag/js?id=${ga4MeasurementId}"]`)) {
        return;
    }

    const googleAdsAccountId = environment.app.gaGoogleAdsAccountId

    // Ensure dataLayer exists (should already be initialized in HTML, but safe guard)
    window.dataLayer = window.dataLayer || [];

    // Ensure gtag function exists (should already be initialized in HTML, but safe guard)
    if (typeof window.gtag !== 'function') {
        window.gtag = function (){window.dataLayer.push(arguments);}
    }

    // Load the gtag.js library
    const gtagLibraryScript = document.createElement('script');
    gtagLibraryScript.async = true;
    gtagLibraryScript.src = `https://www.googletagmanager.com/gtag/js?id=${ga4MeasurementId}`;

    // Config script - dataLayer and gtag are already initialized in HTML
    const gtagConfigScript = document.createElement('script');
    gtagConfigScript.textContent = `
        gtag('js', new Date());
        gtag('config', '${ga4MeasurementId}', { 
            'debug_mode': ${environment.app.gaDebug},
        });
        gtag('config', '${googleAdsAccountId}', { 
            'debug_mode': ${environment.app.gaDebug},
        });
    `;
    document.head.append(gtagLibraryScript);
    document.head.append(gtagConfigScript);
};

/** Reddit Ads pixel — only when {@link environment.app.isProd} and ID is set. */
export const initRedditPixel = () => {
    if (typeof window === 'undefined') return
    if (!environment.app.isProd) return

    const pixelId = environment.app.redditPixelId?.trim()
    if (!pixelId) return

    if (document.querySelector('script[src*="redditstatic.com/ads/pixel.js"]')) return

    const w = window
    const d = document
    if (!w.rdt) {
        const queueRdt = function () {
            queueRdt.sendEvent
                ? queueRdt.sendEvent.apply(queueRdt, arguments)
                : queueRdt.callQueue.push(arguments)
        }
        queueRdt.callQueue = []
        w.rdt = queueRdt
        const t = d.createElement('script')
        t.src = 'https://www.redditstatic.com/ads/pixel.js'
        t.async = true
        const s = d.querySelectorAll('script')[0]
        s.parentNode.insertBefore(t, s)
    }

    w.rdt('init', pixelId)
    w.rdt('track', 'PageVisit')
}

/** Meta (Facebook) pixel — only when {@link environment.app.env} is production and ID is set. */
export const initMetaPixel = () => {
    if (typeof window === 'undefined') return
    if (!environment.app.isProd) return

    const pixelId = environment.app.metaPixelId?.trim()
    if (!pixelId) return

    if (document.querySelector('script[src*="connect.facebook.net"][src*="fbevents.js"]')) return

    const f = window
    const b = document
    const e = 'script'
    const v = 'https://connect.facebook.net/en_US/fbevents.js'
    if (f.fbq) return

    const n = f.fbq = function () {
        n.callMethod
            ? n.callMethod.apply(n, arguments)
            : n.queue.push(arguments)
    }
    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    const t = b.createElement(e)
    t.async = true
    t.src = v
    const s = b.getElementsByTagName(e)[0]
    s.parentNode.insertBefore(t, s)

    f.fbq('init', pixelId)
    f.fbq('track', 'PageView')
}

export const trackEvent = (name, parameters = {}) => {
    if (!isGtagAvailable()) return;

    window.gtag('event', name, {
        page_path: normalizePagePath(window.location.pathname),
        ...parameters,
    });
};

export const trackConversion = (conversionType, options = {}) => {
    const {
        onComplete,
    } = options;

    // Exactly-once wrapper: `event_callback` and the timeout below can both
    // fire, and every early-return path must still run the caller's
    // continuation (it gates navigation — see the TENANT-4068 note below).
    let completed = false;
    const complete = () => {
        if (completed) return;
        completed = true;
        onComplete?.();
    };

    if (!isGtagAvailable()) {
        complete();
        return;
    }

    const codeKey = conversionType === 'property' ? 'propertyActivation' : conversionType;
    const sendTo = environment.adwordConversionCodes[codeKey];

    // Only prod has Adwords labels configured. A missing/placeholder label
    // must skip the ping, never throw — trackConversion sits inside
    // user-facing flows (e.g. between account creation and the OAuth
    // authorize redirect on complete-profile), where a throw aborts the flow.
    if (!isValidSendTo(sendTo)) {
        console.error(`[analytics] Skipping Adwords conversion for "${conversionType}": invalid send_to "${sendTo}". Set the label in the env config for this environment.`);
        complete();
        return;
    }

    // TENANT-4068: a hard navigation (window.location.replace) right after
    // this call tears the page down and cancels the in-flight conversion
    // ping — both July production signups reached PostHog (sendBeacon) but
    // never reached Google Ads; $pageleave landed 322ms after the capture.
    // Callers that hard-navigate after converting MUST pass `onComplete`
    // and navigate inside it; gtag then reports dispatch via
    // `event_callback`, with the timeout as the always-runs bound.
    // SPA `navigate()` calls don't unload the page and need none of this.
    window.gtag('event', 'conversion', {
        currency: 'CAD',
        send_to: sendTo,
        value: 1,
        ...(onComplete ? {
            event_callback: complete,
        } : {}),
        ...options.customParams,
    });

    if (onComplete) {
        setTimeout(complete, ADS_PING_COMPLETE_TIMEOUT_MS);
    }
};

/**
 * Fire a conversion-worthy action to every destination in one call:
 *   - GA4 — a named `event` via gtag (this is what you flag as a Key Event in
 *     the GA4 UI). Unconditional, needs no env config.
 *   - Google Tag Manager — a `dataLayer` custom-event push, so tags can be built
 *     in the GTM UI (including a native Ads conversion) with no further code.
 *     Unconditional, needs no env config.
 *   - Google Ads — a native conversion (the fast bidding path), fired ONLY when
 *     `adsType` is supplied. A native Ads conversion needs a label created in
 *     the Ads account first, so leave `adsType` off until that label exists;
 *     GTM can cover Ads in the meantime.
 *
 * @param {string} name - GA4 event name and GTM dataLayer `event` value.
 * @param {object} [options]
 * @param {object} [options.parameters] - extra params sent to GA4 and the dataLayer.
 * @param {string} [options.adsType] - key in `adwordConversionCodes`; when set,
 *   also fires a native Ads conversion for this action.
 * @param {() => void} [options.onComplete] - Runs exactly once, after the Ads
 *   conversion ping dispatches (or its timeout elapses; immediately when
 *   `adsType` is off). Required when the caller hard-navigates
 *   (window.location) right after converting — do the navigation inside it,
 *   or the browser cancels the in-flight ping (TENANT-4068). SPA `navigate()`
 *   doesn't need it.
 */
export const trackConversionEvent = (name, {
    parameters = {},
    adsType,
    onComplete,
} = {}) => {
    trackEvent(name, parameters);

    if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: name,
            ...parameters,
        });
    }

    if (adsType) {
        trackConversion(adsType, {
            customParams: parameters,
            onComplete,
        });
    } else {
        onComplete?.();
    }
};
